import { getDemoStatus } from '@lms/api-client/backend';
import { Card, CardContent, CardHeader, CardTitle } from '@lms/ui/card';
import { LoaderCircle } from 'lucide-react';
import useSWR from 'swr';
import { useTranslations } from 'use-intl';
import { PageBox, PageHeader, PageTitle } from '@/components/admin/page-box';
import { useSiteAccess } from '@/features/auth/use-auth';
import { useBrand } from '@/features/branding/brand-provider';
import { DemoOverview } from '@/features/demo/demo-overview';
import { NotFound } from '@/routes/not-found';

// Ports app/[brand]/admin/demo/page.tsx. The Next page awaited the current user
// and demo status server-side and used `notFound()` for non-super-admins; here
// the super-admin gate comes from `useSiteAccess`, the status loads via SWR
// (shared key with DemoCard so both revalidate together), and a failed fetch
// degrades to the "unavailable" notice as the Next page did. Renders content
// only; the admin <Shell> is provided by the route tree.
export function Demo() {
  const t = useTranslations('demo');
  const { slug } = useBrand();
  const { access, loading: roleLoading } = useSiteAccess(slug);
  const isSuperAdmin = access?.superAdmin ?? false;

  const {
    data: status,
    isLoading,
    mutate,
  } = useSWR(isSuperAdmin ? ['demo/status'] : null, () => getDemoStatus());

  if (roleLoading) {
    return (
      <div className="flex h-full items-center justify-center py-16">
        <LoaderCircle className="text-primary size-12 animate-spin" />
      </div>
    );
  }

  if (!isSuperAdmin) {
    return <NotFound />;
  }

  return (
    <PageBox>
      <PageHeader>
        <PageTitle>{t('title')}</PageTitle>
      </PageHeader>
      <Card>
        <CardHeader>
          <CardTitle>{t('overview_title')}</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <LoaderCircle className="text-primary size-6 animate-spin" />
            </div>
          ) : status ? (
            <DemoOverview status={status} onChanged={() => mutate()} />
          ) : (
            <p className="text-muted-foreground text-sm">{t('unavailable')}</p>
          )}
        </CardContent>
      </Card>
    </PageBox>
  );
}
